import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { fetchPaydOrdersByUser, fetchPriceImageForPaydOrder } from '../../../services';
import {
  COPIED_STATE_RESET_DELAY,
  getOrderItemKey,
} from '../orderHistory.utils';

const ORDER_HISTORY_PAGE_SIZE = 3;

export const useOrderHistoryController = () => {
  const dispatch = useAppDispatch();
  const userId = useAppSelector((state) => state.user.user?.id);
  const [copiedOrderId, setCopiedOrderId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const copyTimeoutRef = useRef<number | null>(null);
  const fetchedMapRef = useRef<Set<string>>(new Set());
  const {
    orders,
    status,
    error,
    total,
    imageAndPriceByKey,
    imageStatusByKey,
    imageErrorByKey,
  } = useAppSelector((state) => state.paydOrders);
  const isWaitingForOrders = !userId || status === 'idle' || status === 'loading';

  const copyOrderId = useCallback((orderId: string) => {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(orderId);
    }

    if (copyTimeoutRef.current) {
      window.clearTimeout(copyTimeoutRef.current);
    }

    setCopiedOrderId(orderId);
    copyTimeoutRef.current = window.setTimeout(() => {
      setCopiedOrderId(null);
      copyTimeoutRef.current = null;
    }, COPIED_STATE_RESET_DELAY);
  }, []);

  useEffect(() => {
    if (userId) {
      dispatch(fetchPaydOrdersByUser({
        userId,
        take: ORDER_HISTORY_PAGE_SIZE,
        skip: (page - 1) * ORDER_HISTORY_PAGE_SIZE,
      }));
    }
  }, [dispatch, page, userId]);

  const handlePageChange = useCallback((nextPage: number) => {
    setPage(nextPage);
  }, []);

  useEffect(() => {
    if (status !== 'succeeded') {
      return;
    }

    orders.forEach((order) => {
      order.items.forEach((item) => {
        const productId = item.product.id;
        const material = item.material;
        const key = getOrderItemKey(productId, material);
        const alreadyHasData = imageAndPriceByKey[key];

        if (productId && material && !fetchedMapRef.current.has(key) && !alreadyHasData) {
          fetchedMapRef.current.add(key);
          dispatch(fetchPriceImageForPaydOrder({ productId, material }));
        }
      });
    });
  }, [status, orders, dispatch, imageAndPriceByKey]);

  useEffect(() => () => {
    if (copyTimeoutRef.current) {
      window.clearTimeout(copyTimeoutRef.current);
    }
  }, []);

  return {
    copiedOrderId,
    copyOrderId,
    error,
    imageAndPriceByKey,
    imageErrorByKey,
    imageStatusByKey,
    isWaitingForOrders,
    onPageChange: handlePageChange,
    orders,
    page,
    pageSize: ORDER_HISTORY_PAGE_SIZE,
    status,
    total,
  };
};
